import React, { useState } from "react";
import { ChevronDown, ChevronUp, Download, Pause, Play, Phone, X } from "lucide-react";
import Modal from "@/pages/Other/Modal";

const VideoIFrame = ({ url }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="w-full h-full bg-black flex items-center justify-center">
      {isLoading && (
        <div className="text-white">Loading video...</div>
      )}
      <iframe
        src={url}
        className="w-full h-full"
        frameBorder="0"
        allowFullScreen
        onLoad={() => setIsLoading(false)}
        title="Course Video"
      />
    </div>
  );
};

const LearningPath = ({ course, fetchCourse }) => {
  const [openSections, setOpenSections] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [videoSrc, setVideoSrc] = useState("");
  const [currentCourse, setCurrentCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry: "myself",
    experience: "",
    agreedToPolicy: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const courseData = fetchCourse;

  const toggleSection = (index) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const openVideoModal = (videoUrl, courseSection) => {
    setVideoSrc(videoUrl);
    setCurrentCourse(courseSection);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setVideoSrc("");
    setCurrentCourse(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.experience) {
      errors.experience = "Please select your experience level";
    }

    if (!formData.agreedToPolicy) {
      errors.agreedToPolicy = "You must agree to the privacy policy";
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert("Thank you for your interest! We'll contact you soon.");

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        inquiry: "myself",
        experience: "",
        agreedToPolicy: false
      });
      setFormErrors({});
    } catch (error) {
      alert("There was an error submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadSyllabus = () => {
    // Create a mock PDF download
    const element = document.createElement('a');
    const file = new Blob(['Mock Syllabus Content'], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'course-syllabus.pdf';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <section id="programdetails" className="lp-section border-t border-gray-100 bg-[#f8fafc]">
      <div className="lp-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lp-grid">
          {/* Learning Path Section */}
          <div className="lp-timeline-card bg-white/80 border border-gray-100/80 rounded-3xl p-8 shadow-sm">
            <span className="pd-badge mb-4">
              <Download size={11} className="text-blue-500" />
              Syllabus
            </span>
            <h3 className="text-4xl font-extrabold text-gray-900 tracking-tight leading-none mt-2 text-left">Learning Path</h3>
            
            <div className="lp-timeline-wrapper">
              <div className="lp-timeline-line"></div>

              <div className="space-y-6">
                {courseData?.coursesectiondetails?.map((courseSection, index) => (
                  <div key={index} className="lp-timeline-item">
                    <div className="lp-timeline-node font-bold">
                      {index + 1}
                    </div>

                    <div className="lp-step-panel bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
                      <button
                        className="lp-step-header w-full px-6 py-5 flex items-center justify-between gap-4 focus:outline-none"
                        onClick={() => toggleSection(index)}
                      >
                        <span className="lp-step-title text-base font-bold text-gray-800 text-left leading-snug">{courseSection?.sectionName}</span>
                        <div className="flex items-center gap-3">
                          {courseSection.videoLink && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openVideoModal(courseSection.videoLink, courseSection);
                              }}
                              className="lp-preview-btn font-extrabold"
                            >
                              <Play size={11} className="fill-current" />
                              Preview
                            </button>
                          )}
                          {openSections[index] ?
                            <ChevronUp size={18} className="text-gray-400" /> :
                            <ChevronDown size={18} className="text-gray-400" />
                          }
                        </div>
                      </button>

                      {openSections[index] && (
                        <div className="lp-step-body bg-gray-50/50 border-t border-gray-100 p-6">
                          {courseSection?.sectionDescription ? (
                            <div
                              dangerouslySetInnerHTML={{ __html: courseSection.sectionDescription }}
                              className="prose prose-sm max-w-none text-left text-gray-600 font-medium"
                            />
                          ) : (
                            <p className="text-gray-400 italic text-left">Course content details will be available soon.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Contact and Support Section */}
          <div className="lp-support-card bg-gradient-to-br from-[#0f2244] to-[#1e3a6e] rounded-3xl p-10 flex flex-col justify-center items-center text-center shadow-xl border border-white/10 relative overflow-hidden group">
            <span className="lp-support-badge">Admission Office</span>
            <h4 className="text-2xl font-extrabold text-white leading-tight">Have questions about details?</h4>
            <p className="text-blue-100 text-sm mt-3 max-w-xs mx-auto leading-relaxed font-semibold">
              Speak with a program advisor to learn about criteria, schedules, and learning tracks.
            </p>
            
            <div className="mt-8">
              <div className="lp-support-phone text-3xl font-black text-white">95976-84055</div>
              <div className="lp-support-tollfree text-xs text-blue-300 font-bold uppercase tracking-wider mt-1">( Toll Free Support )</div>
            </div>

            <div className="lp-support-icon-wrap w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white mt-8 shadow-inner border border-white/10 group-hover:scale-108 transition-all duration-300">
              <Phone size={24} className="stroke-[2]" />
            </div>
          </div>
        </div>

        {/* Video Modal */}
        {showModal && (
          <Modal isOpen={showModal} onClose={closeModal}>
            <div className="bg-gray-900 text-white w-full max-w-6xl rounded-lg shadow-xl relative h-[90vh] overflow-hidden">
              <div className="h-full overflow-y-scroll">
                {/* Video Panel */}
                <div className="w-full p-6">
                  <h2 className="text-2xl font-bold mb-4">{currentCourse?.sectionName}</h2>
                  <div className="relative w-full h-[280px] md:h-[400px] rounded-lg overflow-hidden bg-black border border-gray-700">
                    <VideoIFrame url={videoSrc} />
                  </div>
                </div>

                {/* Course Info Panel */}
                <div className="w-full bg-gray-800 border-l border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-300 mb-4">Course Information</h3>

                  {currentCourse && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-white mb-2">{currentCourse.sectionName}</h4>
                        {currentCourse.sectionDescription && (
                          <div
                            dangerouslySetInnerHTML={{ __html: currentCourse.sectionDescription }}
                            className="text-gray-300 text-sm prose prose-invert prose-sm max-w-none"
                          />
                        )}
                      </div>

                      <div className="pt-4 border-t border-gray-700">
                        <p className="text-gray-400 text-sm">
                          This is a preview of the course content. Enroll to access the full curriculum.
                        </p>
                      </div>

                      {/* Next Videos Section */}
                      <div className="pt-4 border-t border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-300 mb-3">Up Next</h4>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {(() => {
                            // Find current video index
                            const currentIndex = courseData?.coursesectiondetails?.findIndex(
                              section => section.sectionName === currentCourse.sectionName
                            );
                            // Get next 4 videos
                            const nextVideos = courseData?.coursesectiondetails?.slice(currentIndex + 1, currentIndex + 5) || [];

                            if (nextVideos.length === 0) {
                              return (
                                <p className="text-gray-500 text-sm italic">No more videos available</p>
                              );
                            }

                            return nextVideos.map((video, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors cursor-pointer"
                                onClick={() => {
                                  setVideoSrc(video.videoLink);
                                  setCurrentCourse(video);
                                }}
                              >
                                <button className="flex-shrink-0">
                                  <Play className="w-4 h-4 text-blue-400" />
                                </button>

                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-white text-sm truncate">
                                    {video.sectionName}
                                  </p>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </section>
  );
};

export default LearningPath;
